import React, { forwardRef } from "react";
import { formatDuration, MatchResult, investmentInCny } from "../../data/assessment";
import { nvfoLogoBlue, nvfoWechatQR, nvfoWhatsappQR } from "../../assets/imgExport";

type Props = {
  name: string;
  matches: MatchResult[];
};

const AssessmentReportTemplate = forwardRef<HTMLDivElement, Props>(
  ({ name, matches }, ref) => {
    const today = new Date().toLocaleDateString("zh-CN");

    return (
      <div
        ref={ref}
        style={{
          display: "none",
          position: "fixed",
          top: 0,
          left: 0,
          zIndex: 9999,
          pointerEvents: "none",
          width: "297mm",
          minHeight: "210mm",
          background: "#fff",
          fontFamily: "'Noto Sans SC', 'Noto Serif SC', sans-serif",
          padding: "14mm 14mm",
          boxSizing: "border-box",
          color: "#232d39",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "2px solid #b89355",
            paddingBottom: "6mm",
            marginBottom: "6mm",
          }}
        >
          <div>
            <img src={nvfoLogoBlue} alt="NVFO" style={{ height: "9mm", objectFit: "contain" }} />
            <p style={{ fontSize: "9pt", color: "#89939f", marginTop: "3mm" }}>
              New Vision Family Office
            </p>
          </div>
          <div style={{ textAlign: "right" }}>
            <p style={{ fontSize: "9pt", color: "#89939f" }}>生成日期：{today}</p>
            {name && <p style={{ fontSize: "9pt", color: "#89939f" }}>致：{name}</p>}
          </div>
        </div>

        <h1 style={{ fontSize: "18pt", fontWeight: 700, margin: "0 0 3mm", color: "#232d39" }}>
          移民方案定制报告
        </h1>
        <p style={{ fontSize: "9.5pt", color: "#5f5e5a", marginBottom: "6mm", lineHeight: 1.6 }}>
          根据您在测评中提供的预算、目标、意向地区与办理时效，AI 智能匹配系统从 NVFO
          覆盖的20多个国家与地区身份规划方案中，为您筛选出以下最相关的推荐方案。
        </p>

        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: "9pt",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: "24%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "36%" }} />
          </colgroup>
          <thead>
            <tr style={{ background: "#f4f1ea" }}>
              {["国家 / 项目", "项目类型", "投资门槛", "AI 匹配度", "匹配原因"].map((h) => (
                <th
                  key={h}
                  style={{
                    textAlign: "left",
                    padding: "3mm 3mm",
                    fontSize: "8.5pt",
                    fontWeight: 700,
                    color: "#5f5e5a",
                    borderBottom: "1.5px solid #b89355",
                    letterSpacing: "0.03em",
                  }}
                >
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {matches.map((match, index) => {
              const cny = investmentInCny(match.data);
              return (
                <tr
                  key={match.slug + match.category}
                  style={{
                    borderBottom: "1px solid #d8dbdf",
                    background: index % 2 === 1 ? "#fafaf8" : "#fff",
                    verticalAlign: "top",
                  }}
                >
                  <td style={{ padding: "3.5mm 3mm" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "3mm" }}>
                      <img
                        src={match.data.image.flag}
                        alt={match.data.country}
                        style={{
                          width: "16mm",
                          height: "11mm",
                          objectFit: "contain",
                          background: "#fff",
                          border: "1px solid #d8dbdf",
                          borderRadius: "1.5mm",
                          flexShrink: 0,
                        }}
                      />
                      <div>
                        <p style={{ fontSize: "10pt", fontWeight: 700, margin: 0 }}>
                          {index + 1}. {match.data.country}
                        </p>
                        <p style={{ fontSize: "8.5pt", color: "#89939f", margin: "1mm 0 0" }}>
                          {match.data.immigrationType.title}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: "3.5mm 3mm", fontSize: "8.5pt", color: "#232d39" }}>
                    {match.data.immigrationType.type}
                  </td>
                  <td style={{ padding: "3.5mm 3mm", fontSize: "8.5pt", color: "#232d39" }}>
                    {cny > 0 ? `约¥${Math.round(cny / 10000)}万元起` : "无固定投资门槛"}
                    <br />
                    <span style={{ color: "#89939f" }}>
                      办理周期 {formatDuration(match.data.process.duration)}
                    </span>
                  </td>
                  <td style={{ padding: "3.5mm 3mm" }}>
                    <p style={{ fontSize: "11pt", fontWeight: 700, color: "#b89355", margin: "0 0 1.5mm" }}>
                      {match.score}%
                    </p>
                    <div
                      style={{
                        width: "100%",
                        height: "1.6mm",
                        background: "#f0ede5",
                        borderRadius: "1mm",
                        overflow: "hidden",
                      }}
                    >
                      <div
                        style={{
                          width: `${match.score}%`,
                          height: "100%",
                          background: "#b89355",
                        }}
                      />
                    </div>
                  </td>
                  <td style={{ padding: "3.5mm 3mm" }}>
                    <ul style={{ fontSize: "8.5pt", color: "#232d39", margin: 0, paddingLeft: "4mm" }}>
                      {match.reasons.map((reason, i) => (
                        <li key={i} style={{ marginBottom: i === match.reasons.length - 1 ? 0 : "1mm" }}>
                          {reason}
                        </li>
                      ))}
                    </ul>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div
          style={{
            marginTop: "8mm",
            paddingTop: "5mm",
            borderTop: "1px solid #d8dbdf",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ fontSize: "9pt", color: "#89939f" }}>
            <p style={{ margin: "0 0 1.5mm" }}>
              本报告由 AI 智能匹配系统生成，仅供参考，精准的匹配方案请以顾问一对一评估为准。
            </p>
            <p style={{ margin: 0 }}>fo@newvgroup.com · +44 (0)20 3907 4025</p>
          </div>
          <div style={{ display: "flex", gap: "6mm" }}>
            <div style={{ textAlign: "center" }}>
              <img
                src={nvfoWechatQR}
                alt="微信咨询二维码"
                style={{ height: "18mm", width: "auto", border: "1px solid #d8dbdf", borderRadius: "1.5mm" }}
              />
              <p style={{ fontSize: "7.5pt", color: "#89939f", margin: "1.5mm 0 0" }}>微信咨询</p>
            </div>
            <div style={{ textAlign: "center" }}>
              <img
                src={nvfoWhatsappQR}
                alt="WhatsApp咨询二维码"
                style={{ height: "18mm", width: "auto", border: "1px solid #d8dbdf", borderRadius: "1.5mm" }}
              />
              <p style={{ fontSize: "7.5pt", color: "#89939f", margin: "1.5mm 0 0" }}>WhatsApp咨询</p>
            </div>
          </div>
        </div>
      </div>
    );
  },
);

AssessmentReportTemplate.displayName = "AssessmentReportTemplate";

export default AssessmentReportTemplate;
